import React from 'react';
import { useTheme } from 'styled-components';
import { IDiscountState } from './useDiscountEditing';
import { RoundPrice } from '../../../logic/Utility/CalculationUtilities';
import { CropKey } from './CompetitiveAllowanceMain';
import { formatCurrency, IPricingBreakdown } from './pricingMatrix';

/** Summary banner: Subtotal → −Early Order → −Local Grower → −Competitive Allowance → Total, from the discount matrix. */
export function PlanDiscountSummaryRow(props: {
	planName: string;
	discountState: IDiscountState;
	breakdown?: IPricingBreakdown;
	/** Unsaved edits - the breakdown is a client-side estimate until Save. */
	estimated?: boolean;
	cornExists: boolean;
	soyExists: boolean;
})
{
	const theme = useTheme();
	const { planName, discountState, breakdown, estimated, cornExists, soyExists } = props;

	// List price per unit is averaged from the plan's rows; the matrix has no pre-discount per-bag figure.
	const averagePricePerBag = (cropKey: CropKey) =>
	{
		const products = discountState.discounts?.[cropKey].hybrids.flatMap(h => h.Products) ?? [];
		const quantity = products.reduce((acc, product) => acc + product.Quantity, 0);
		return RoundPrice(products.reduce((acc, product) => acc + (product.PricePerBag * product.Quantity), 0) / quantity || 0);
	};

	const labelStyle: React.CSSProperties = { fontSize: theme.fontSizes.extraSmall, fontWeight: theme.fontWeights.normal };
	const column = (label: string, value: string, width: string, options?: { hidden?: boolean; discount?: boolean; emphasis?: boolean }) =>
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				textAlign: 'right',
				width,
				visibility: options?.hidden ? 'hidden' : 'visible',
				paddingLeft: options?.discount ? 10 : undefined,
				fontSize: options?.emphasis ? theme.fontSizes.normal : undefined,
			}}
		>
			<div style={labelStyle}>{label}</div>
			<div>{value}</div>
		</div>;

	const amount = (value: number | undefined) => typeof value === 'number' ? formatCurrency(value) : '—';

	return <div className='CompetitiveAllowance_SummaryTable' style={{ display: 'flex', alignItems: 'start', height: 'auto', marginTop: 25 }}>
		<div
			style={{
				backgroundColor: theme.colors.primary,
				color: theme.colors.white,
				width: '100%',
				borderRadius: 4,
				paddingTop: 10,
				paddingBottom: 10,
				paddingRight: 15,
				paddingLeft: 15,
				display: 'flex',
				alignItems: 'flex-end',
				fontWeight: theme.fontWeights.bold
			}}
		>
			<div style={{ display: 'flex', flexDirection: 'column', width: '20%' }}>
				<div style={labelStyle}>Name</div>
				<div>{planName}</div>
			</div>
			{column('Corn/Unit', formatCurrency(averagePricePerBag('Corn')), '9%', { hidden: !cornExists })}
			{column('Soy/Unit', formatCurrency(averagePricePerBag('Soybean')), '8%', { hidden: !soyExists })}
			{column('Subtotal', amount(breakdown?.listTotal), '11%')}
			<div style={{ flex: '1 1 auto' }} />
			{column('− Early Order', amount(breakdown ? breakdown.orderDeadline?.discountTotal ?? 0 : undefined), '10%', { discount: true })}
			{column('− Local Grower', amount(breakdown ? breakdown.localGrower?.discountTotal ?? 0 : undefined), '10%', { discount: true })}
			{column('− Competitive Allowance', amount(breakdown?.competitiveAllowance.discountTotal), '13%', { discount: true })}
			{column(estimated ? 'Total (unsaved estimate)' : 'Total', amount(breakdown?.netTotal), '13%', { emphasis: true })}
		</div>
	</div>;
}
